import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast("Welcome to the dispatch");
      setEmail("");
    } catch (err) {
      toast("Something went quiet. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-ink text-bone mt-32" data-testid="site-footer">
      {/* Top — large brand mark + newsletter */}
      <div className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-charcoal">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="overline text-stone mb-6">The dispatch · monthly</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              A quiet letter,
              <br />
              <em className="italic text-bone/70">at the new moon.</em>
            </h2>
            <p className="mt-6 max-w-md text-bone/60 text-sm md:text-base leading-relaxed">
              One reflection. One ritual. One ingredient. No promotions,
              no urgency. Unsubscribe in a single click.
            </p>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <form onSubmit={onSubmit} className="w-full" data-testid="newsletter-form">
              <div className="flex items-end border-b border-bone/40 focus-within:border-bone transition-colors">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="newsletter-email"
                  className="flex-1 bg-transparent text-bone placeholder:text-bone/40 py-3 outline-none text-base font-light"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="flex items-center gap-2 text-[0.72rem] tracking-[0.22em] uppercase font-medium py-3 px-2 hover:text-bone/70 transition-colors"
                >
                  {loading ? "..." : "Receive"}
                  <ArrowUpRight size={14} />
                </button>
              </div>
              <p className="mt-3 text-xs text-bone/40">By subscribing you agree to receive our monthly dispatch.</p>
            </form>
          </div>
        </div>
      </div>

      {/* Middle — link columns */}
      <div className="px-6 md:px-12 lg:px-16 py-16 grid grid-cols-2 md:grid-cols-4 gap-8 border-b border-charcoal">
        <div>
          <p className="overline text-stone mb-5">Read</p>
          <ul className="space-y-2 text-sm text-bone/80">
            <li><Link to="/journal" className="link-underline">Journal</Link></li>
            <li><Link to="/rituals" className="link-underline">Rituals</Link></li>
            <li><Link to="/library" className="link-underline">Library</Link></li>
          </ul>
        </div>
        <div>
          <p className="overline text-stone mb-5">Rituals</p>
          <ul className="space-y-2 text-sm text-bone/80">
            <li><Link to="/rituals/rest" className="link-underline">Rest</Link></li>
            <li><Link to="/rituals" className="link-underline">Ground · soon</Link></li>
            <li><Link to="/rituals" className="link-underline">Restore · soon</Link></li>
            <li><Link to="/rituals" className="link-underline">Daily · soon</Link></li>
          </ul>
        </div>
        <div>
          <p className="overline text-stone mb-5">House</p>
          <ul className="space-y-2 text-sm text-bone/80">
            <li><Link to="/philosophy" className="link-underline">Philosophy</Link></li>
            <li><a href="#" className="link-underline">Contact</a></li>
            <li><a href="#" className="link-underline">Stockists</a></li>
          </ul>
        </div>
        <div>
          <p className="overline text-stone mb-5">Elsewhere</p>
          <ul className="space-y-2 text-sm text-bone/80">
            <li><a href="#" className="link-underline">Instagram</a></li>
            <li><a href="#" className="link-underline">Substack</a></li>
            <li><a href="#" className="link-underline">Spotify</a></li>
          </ul>
        </div>
      </div>

      {/* Giant wordmark */}
      <div className="px-6 md:px-12 lg:px-16 py-8 overflow-hidden">
        <div className="font-display text-[18vw] md:text-[14vw] lg:text-[11vw] leading-[0.85] tracking-[-0.04em] text-bone/15 select-none">
          still <em className="italic">form.</em>
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-16 py-6 flex flex-col md:flex-row md:items-center justify-between gap-2 border-t border-charcoal">
        <p className="text-xs tracking-[0.18em] uppercase text-stone">
          © {new Date().getFullYear()} Still Form
        </p>
        <p className="text-xs tracking-[0.18em] uppercase text-stone">
          Made in care. Read slowly.
        </p>
      </div>
    </footer>
  );
}
